import * as React from "react";
import { useRef, useState, useCallback, useEffect, useMemo } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Check, ChevronDown, X, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface GroupedSelectItem {
  label: string;
  value: string;
}

export interface GroupedSelectGroup {
  group: string;
  items: GroupedSelectItem[];
}

type MultiProps = {
  mode: "multi";
  value: string[];
  onChange: (value: string[]) => void;
};

type SingleProps = {
  mode: "single";
  value: string | null;
  onChange: (value: string | null) => void;
};

type CommonProps = {
  options: GroupedSelectGroup[];
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  id?: string;
};

export type GroupedSelectProps = CommonProps & (MultiProps | SingleProps);

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function normalise(s: string) {
  return s.toLowerCase().trim();
}

function filterGroups(
  options: GroupedSelectGroup[],
  query: string
): GroupedSelectGroup[] {
  if (!query) return options;
  const q = normalise(query);
  return options
    .map((g) => {
      const groupMatches = normalise(g.group).includes(q);
      const items = groupMatches
        ? g.items
        : g.items.filter((i) => normalise(i.label).includes(q));
      return { ...g, items };
    })
    .filter((g) => g.items.length > 0);
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

/** Removable chip shown in multi-mode trigger */
function Chip({
  label,
  onRemove,
  disabled,
}: {
  label: string;
  onRemove: () => void;
  disabled?: boolean;
}) {
  return (
    /* A chip here always represents a SELECTED value, so it wears the palette's
       chosen pairing — teal tint on teal text, the same one the Verified badge
       and the selected Capsule tab use — rather than the card-white it had,
       which did not read as "chosen". The remove control is a small circle that
       hugs the X (was a 20px box with a tiny icon floating in the middle, which
       is the wide untidy gap that was reported), and pr-1 pulls it close to the
       pill edge without touching it. */
    <span
      data-capsule="chip"
      className="inline-flex items-center gap-1 rounded-full border border-success/30 bg-secondary pl-2.5 pr-1 py-1 text-xs font-bold text-success"
    >
      {label}
      {!disabled && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="grid h-4 w-4 place-items-center rounded-full text-success/70 transition-colors hover:bg-success/15 hover:text-success focus:outline-none focus-visible:ring-2 focus-visible:ring-success"
          aria-label={`Remove ${label}`}
        >
          <X className="h-3 w-3" strokeWidth={2.5} />
        </button>
      )}
    </span>
  );
}

/** Three-state checkbox icon */
type CheckState = "none" | "partial" | "all";

function TriStateIcon({ state }: { state: CheckState }) {
  if (state === "all") {
    return (
      <span className="flex h-4 w-4 items-center justify-center rounded-sm border border-primary bg-primary text-primary-foreground">
        <Check className="h-3 w-3" strokeWidth={3} />
      </span>
    );
  }
  if (state === "partial") {
    return (
      <span className="flex h-4 w-4 items-center justify-center rounded-sm border border-primary bg-primary/20">
        <Minus className="h-3 w-3 text-primary" strokeWidth={3} />
      </span>
    );
  }
  return (
    <span className="flex h-4 w-4 items-center justify-center rounded-sm border border-input bg-background" />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export const GroupedSelect = React.forwardRef<HTMLButtonElement, GroupedSelectProps>(
  (props, ref) => {
    const {
      options,
      placeholder = "Select…",
      disabled = false,
      clearable = false,
      className,
      id,
    } = props;

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const searchRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const [focusedIndex, setFocusedIndex] = useState<number>(-1);

    // Reset query when closed
    useEffect(() => {
      if (!open) setQuery("");
    }, [open]);

    // Focus search when opened
    useEffect(() => {
      if (open) {
        requestAnimationFrame(() => {
          searchRef.current?.focus();
        });
      }
    }, [open]);

    const filtered = useMemo(() => filterGroups(options, query), [options, query]);

    // Flat list of all visible items (for keyboard navigation)
    const flatItems = useMemo(
      () => filtered.flatMap((g) => g.items),
      [filtered]
    );

    // ── Value helpers ────────────────────────────────────────────────────────

    const isSelected = useCallback(
      (v: string): boolean => {
        if (props.mode === "multi") return props.value.includes(v);
        return props.value === v;
      },
      [props.mode, props.value]  
    );

    const toggle = useCallback(
      (v: string) => {
        if (props.mode === "multi") {
          props.onChange(
            props.value.includes(v)
              ? props.value.filter((x) => x !== v)
              : [...props.value, v]
          );
        } else {
          props.onChange(props.value === v ? null : v);
          setOpen(false);
        }
      },
      [props]  
    );

    const getGroupState = useCallback(
      (items: GroupedSelectItem[]): CheckState => {
        if (props.mode !== "multi") return "none";
        const selectedCount = items.filter((i) =>
          props.value.includes(i.value)
        ).length;
        if (selectedCount === 0) return "none";
        if (selectedCount === items.length) return "all";
        return "partial";
      },
      [props.mode, props.value]  
    );

    const toggleGroup = useCallback(
      (items: GroupedSelectItem[]) => {
        if (props.mode !== "multi") return;
        const state = getGroupState(items);
        const itemValues = items.map((i) => i.value);
        if (state === "all") {
          // deselect all visible in group
          props.onChange(props.value.filter((v) => !itemValues.includes(v)));
        } else {
          // select all visible in group
          const next = [...props.value];
          itemValues.forEach((v) => {
            if (!next.includes(v)) next.push(v);
          });
          props.onChange(next);
        }
      },
      [props, getGroupState]  
    );

    const clearAll = useCallback(() => {
      if (props.mode === "multi") props.onChange([]);
      else props.onChange(null);
    }, [props]);  

    // ── Keyboard navigation ──────────────────────────────────────────────────

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (!open) {
          if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
          }
          return;
        }

        switch (e.key) {
          case "Escape":
            e.preventDefault();
            setOpen(false);
            break;
          case "ArrowDown":
            e.preventDefault();
            setFocusedIndex((i) => Math.min(i + 1, flatItems.length - 1));
            break;
          case "ArrowUp":
            e.preventDefault();
            setFocusedIndex((i) => Math.max(i - 1, 0));
            break;
          case "Enter": {
            e.preventDefault();
            const item = flatItems[focusedIndex];
            if (item) toggle(item.value);
            break;
          }
          default:
            break;
        }
      },
      [open, flatItems, focusedIndex, toggle]
    );

    // Scroll focused item into view
    useEffect(() => {
      if (focusedIndex >= 0 && listRef.current) {
        const el = listRef.current.querySelector<HTMLElement>(
          `[data-focused="true"]`
        );
        el?.scrollIntoView({ block: "nearest" });
      }
    }, [focusedIndex]);

    // ── Trigger label ────────────────────────────────────────────────────────

    const selectedItems = useMemo(() => {
      if (props.mode === "multi") {
        return options
          .flatMap((g) => g.items)
          .filter((i) => props.value.includes(i.value));
      }
      if (props.value === null) return [];
      const found = options
        .flatMap((g) => g.items)
        .find((i) => i.value === props.value);
      return found ? [found] : [];
    }, [options, props.mode, props.value]);  

    const hasValue =
      props.mode === "multi" ? props.value.length > 0 : props.value !== null;

    // ── Render ───────────────────────────────────────────────────────────────

    return (
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        {/* Trigger */}
        <PopoverPrimitive.Trigger asChild>
          <button
            ref={ref}
            id={id}
            type="button"
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            onKeyDown={handleKeyDown}
            className={cn(
              "flex min-h-[44px] w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "hover:border-primary/60 transition-colors",
              className
            )}
          >
            {/* Left: chips (multi) or label (single) */}
            <span className="flex flex-1 flex-wrap gap-1 overflow-hidden text-left">
              {!hasValue && (
                <span className="text-muted-foreground truncate">
                  {placeholder}
                </span>
              )}
              {props.mode === "multi" && hasValue &&
                selectedItems.map((item) => (
                  <Chip
                    key={item.value}
                    label={item.label}
                    disabled={disabled}
                    onRemove={() => toggle(item.value)}
                  />
                ))}
              {props.mode === "single" && hasValue && (
                <span className="truncate">{selectedItems[0]?.label}</span>
              )}
            </span>

            {/* Right: chevron only. The bare inline clear-✕ that used to float
                here read as a stray control beside the chevron; bulk clearing
                lives in the panel footer as a labelled "Clear all", and each
                chip carries its own remove, so nothing is lost. */}
            <span className="ml-2 flex shrink-0 items-center">
              <ChevronDown
                className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform duration-150",
                  open && "rotate-180"
                )}
              />
            </span>
          </button>
        </PopoverPrimitive.Trigger>

        {/* Panel */}
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            sideOffset={6}
            align="start"
            className={cn(
              "z-50 min-w-[var(--radix-popover-trigger-width)] max-w-[min(480px,calc(100vw-2rem))]",
              "rounded-md border bg-popover text-popover-foreground shadow-lg",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
              "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2"
            )}
          >
            {/* Search */}
            <div className="border-b px-3 py-2">
              <input
                ref={searchRef}
                type="text"
                placeholder="Search…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setFocusedIndex(-1);
                }}
                onKeyDown={handleKeyDown}
                className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                aria-label="Search options"
              />
            </div>

            {/* Options list */}
            <div
              ref={listRef}
              role="listbox"
              aria-multiselectable={props.mode === "multi"}
              className="max-h-60 overflow-y-auto p-1"
            >
              {filtered.length === 0 ? (
                <p className="px-3 py-4 text-center text-sm text-muted-foreground">
                  No results
                </p>
              ) : (
                filtered.map((group) => {
                  const groupState =
                    props.mode === "multi"
                      ? getGroupState(group.items)
                      : "none";
                  const selectedInGroup =
                    props.mode === "multi"
                      ? group.items.filter((i) => props.value.includes(i.value)).length
                      : 0;

                  return (
                    <div key={group.group} role="group" aria-label={group.group}>
                      {/* Group header */}
                      <div
                        className={cn(
                          "flex items-center justify-between gap-2 px-2 py-1.5",
                          props.mode === "multi" &&
                            "cursor-pointer rounded-sm hover:bg-accent"
                        )}
                        onClick={() =>
                          props.mode === "multi" && toggleGroup(group.items)
                        }
                        role={props.mode === "multi" ? "button" : undefined}
                        tabIndex={-1}
                        aria-label={
                          props.mode === "multi"
                            ? `Toggle all ${group.group}`
                            : undefined
                        }
                      >
                        <div className="flex items-center gap-2">
                          {props.mode === "multi" && (
                            <TriStateIcon state={groupState} />
                          )}
                          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {group.group}
                          </span>
                        </div>
                        {props.mode === "multi" && (
                          <span className="text-xs text-muted-foreground">
                            {selectedInGroup}/{group.items.length}
                          </span>
                        )}
                      </div>

                      {/* Items */}
                      {group.items.map((item) => {
                        const selected = isSelected(item.value);
                        const flatIdx = flatItems.indexOf(item);
                        const focused = flatIdx === focusedIndex;

                        return (
                          <div
                            key={item.value}
                            role="option"
                            aria-selected={selected}
                            data-focused={focused || undefined}
                            onClick={() => toggle(item.value)}
                            className={cn(
                              "flex cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-sm",
                              "hover:bg-accent hover:text-accent-foreground",
                              "focus:bg-accent focus:outline-none",
                              selected && "font-medium text-primary",
                              focused && "bg-accent text-accent-foreground"
                            )}
                          >
                            {props.mode === "multi" ? (
                              <span
                                className={cn(
                                  "flex h-4 w-4 items-center justify-center rounded-sm border shrink-0",
                                  selected
                                    ? "border-primary bg-primary text-primary-foreground"
                                    : "border-input bg-background"
                                )}
                              >
                                {selected && <Check className="h-3 w-3" strokeWidth={3} />}
                              </span>
                            ) : (
                              <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                                {selected && <Check className="h-4 w-4 text-primary" />}
                              </span>
                            )}
                            <span className="truncate">{item.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer: selection count + the labelled clear that replaced the
                stray trigger ✕. Now shown for single mode too when clearable,
                so removing that inline cross did not strip single-mode selects
                of any way to clear. */}
            {hasValue && (props.mode === "multi" || clearable) && (
              <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
                <span>
                  {props.mode === "multi" ? `${props.value.length} selected` : "Selected"}
                </span>
                {clearable && (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="text-destructive hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    Clear all
                  </button>
                )}
              </div>
            )}
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    );
  }
);

GroupedSelect.displayName = "GroupedSelect";
